import { CSSProperties, ReactNode, useLayoutEffect, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import { JOB_COLUMN_WIDTH } from './constants';

interface InfiniteListProps<T> {
  estimatedItemSize?: number;
  hasNextPage: boolean;
  itemCount?: number;
  itemSize: number | ((index: number) => number);
  items: T[];
  loadMoreItems: () => void;
  renderItem: (index: number, style: CSSProperties) => ReactNode;
  style?: CSSProperties;
  width?: number;
}

const InfiniteList = <T,>({
  estimatedItemSize,
  hasNextPage,
  itemCount,
  itemSize,
  items,
  loadMoreItems,
  renderItem,
  style,
  width,
}: InfiniteListProps<T>) => {
  const variableSizeListRef = useRef<VariableSizeList | null>(null);

  useLayoutEffect(() => {
    variableSizeListRef.current?.resetAfterIndex(0);
  }, [items]);

  // Only load 1 page of items at a time.
  // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
  const loadNextPage = hasNextPage
    ? () => {
        loadMoreItems();
      }
    : () => {};

  return (
    <AutoSizer>
      {({ height }) => (
        <InfiniteLoader
          isItemLoaded={(index) => !hasNextPage || index < items.length + 1}
          loadMoreItems={loadNextPage}
          itemCount={10000}
          threshold={15}
        >
          {({ onItemsRendered, ref }) =>
            typeof itemSize === 'function' ? (
              <VariableSizeList
                estimatedItemSize={estimatedItemSize}
                height={height || 0}
                itemCount={itemCount ?? items.length}
                itemSize={itemSize}
                width={width ?? JOB_COLUMN_WIDTH}
                onItemsRendered={onItemsRendered}
                style={style}
                ref={(list) => {
                  variableSizeListRef.current = list;
                  ref(list);
                }}
              >
                {({ index, style }) => renderItem(index, style)}
              </VariableSizeList>
            ) : (
              <FixedSizeList
                height={height || 0}
                itemCount={itemCount ?? items.length}
                itemSize={itemSize}
                width={width ?? JOB_COLUMN_WIDTH}
                onItemsRendered={onItemsRendered}
                style={style}
                ref={ref}
              >
                {({ index, style }) => renderItem(index, style)}
              </FixedSizeList>
            )
          }
        </InfiniteLoader>
      )}
    </AutoSizer>
  );
};

export default InfiniteList;
